import { useEffect, useRef, useState } from 'react'

const INITIAL_VARS = {
  '--foil-x': '50%',
  '--foil-y': '50%',
  '--foil-tilt-x': '0deg',
  '--foil-tilt-y': '0deg',
}

function supportsReducedMotion() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function FoilParallaxCard({ children, className = '', ...props }) {
  const frameRef = useRef(null)
  const [reducedMotion, setReducedMotion] = useState(supportsReducedMotion)
  const [active, setActive] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReducedMotion(query.matches)
    update()
    query.addEventListener?.('change', update)
    return () => query.removeEventListener?.('change', update)
  }, [])

  const reset = () => {
    const frame = frameRef.current
    if (!frame) return
    Object.entries(INITIAL_VARS).forEach(([name, value]) => frame.style.setProperty(name, value))
    setActive(false)
  }

  const handlePointerMove = (event) => {
    if (reducedMotion) return
    const frame = frameRef.current
    if (!frame) return
    const bounds = frame.getBoundingClientRect()
    const x = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width))
    const y = Math.max(0, Math.min(1, (event.clientY - bounds.top) / bounds.height))
    frame.style.setProperty('--foil-x', `${(x * 100).toFixed(1)}%`)
    frame.style.setProperty('--foil-y', `${(y * 100).toFixed(1)}%`)
    frame.style.setProperty('--foil-tilt-x', `${((0.5 - y) * 7).toFixed(2)}deg`)
    frame.style.setProperty('--foil-tilt-y', `${((x - 0.5) * 7).toFixed(2)}deg`)
    setActive(true)
  }

  return (
    <div
      {...props}
      ref={frameRef}
      data-testid="foil-parallax-card"
      data-active={active ? 'true' : 'false'}
      className={`group relative isolate transform-gpu overflow-hidden rounded-3xl transition-[transform,box-shadow] duration-300 ease-out ${className}`.trim()}
      style={{
        ...INITIAL_VARS,
        transform: reducedMotion
          ? 'translateZ(0)'
          : 'perspective(1100px) rotateX(var(--foil-tilt-x)) rotateY(var(--foil-tilt-y)) translateZ(0)',
        boxShadow: active
          ? '0 26px 55px -24px rgba(84, 49, 3, 0.65), 0 0 0 1px rgba(245, 215, 123, 0.62)'
          : '0 18px 42px -26px rgba(84, 49, 3, 0.4), 0 0 0 1px rgba(245, 215, 123, 0.4)',
        touchAction: 'pan-y',
      }}
      onPointerEnter={() => setActive(true)}
      onPointerMove={handlePointerMove}
      onPointerLeave={reset}
      onPointerCancel={reset}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-20 rounded-[inherit] transition-opacity duration-300"
        style={{
          opacity: active ? 0.82 : 0.48,
          mixBlendMode: 'screen',
          background: [
            'radial-gradient(circle at var(--foil-x) var(--foil-y), rgba(255, 250, 211, 0.86), rgba(255, 217, 92, 0.18) 13%, transparent 32%)',
            'linear-gradient(122deg, transparent 18%, rgba(255, 236, 153, 0.1) 35%, rgba(255, 247, 197, 0.72) 48%, rgba(197, 126, 12, 0.12) 58%, transparent 76%)',
          ].join(', '),
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-[1px] z-20 rounded-[calc(1.5rem-1px)] border border-[#f7dc83]/60"
        style={{
          boxShadow: 'inset 0 0 0 1px rgba(255, 244, 180, 0.25), inset 0 0 24px rgba(213, 145, 24, 0.16)',
        }}
      />
      <div className="relative z-10">{children}</div>
    </div>
  )
}
